import type { GridRgb } from './config'

export interface BoxSpec {
  readonly up: number
  readonly down: number
  readonly left: number
  readonly right: number
  readonly shade: number
  readonly fillEighths: number
  readonly fillEdge: number
}

export interface BoxRect {
  readonly kind: 'rect'
  readonly layer: 'content'
  readonly primitive: 'box'
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly color: GridRgb
  readonly alpha: number
}

const L = 1
const H = 2
const D = 3

const LINE_SPECS = new Map<number, readonly [number, number, number, number]>([
  [0x2500, [0, 0, L, L]], [0x2501, [0, 0, H, H]],
  [0x2502, [L, L, 0, 0]], [0x2503, [H, H, 0, 0]],
  [0x250c, [0, L, 0, L]], [0x250d, [0, L, 0, H]], [0x250e, [0, H, 0, L]], [0x250f, [0, H, 0, H]],
  [0x2510, [0, L, L, 0]], [0x2511, [0, L, H, 0]], [0x2512, [0, H, L, 0]], [0x2513, [0, H, H, 0]],
  [0x2514, [L, 0, 0, L]], [0x2515, [L, 0, 0, H]], [0x2516, [H, 0, 0, L]], [0x2517, [H, 0, 0, H]],
  [0x2518, [L, 0, L, 0]], [0x2519, [L, 0, H, 0]], [0x251a, [H, 0, L, 0]], [0x251b, [H, 0, H, 0]],
  [0x251c, [L, L, 0, L]], [0x2523, [H, H, 0, H]],
  [0x2524, [L, L, L, 0]], [0x252b, [H, H, H, 0]],
  [0x252c, [0, L, L, L]], [0x2533, [0, H, H, H]],
  [0x2534, [L, 0, L, L]], [0x253b, [H, 0, H, H]],
  [0x253c, [L, L, L, L]], [0x254b, [H, H, H, H]],
  [0x2550, [0, 0, D, D]], [0x2551, [D, D, 0, 0]],
  [0x2554, [0, D, 0, D]], [0x2557, [0, D, D, 0]],
  [0x255a, [D, 0, 0, D]], [0x255d, [D, 0, D, 0]],
  [0x2560, [D, D, 0, D]], [0x2563, [D, D, D, 0]],
  [0x2566, [0, D, D, D]], [0x2569, [D, 0, D, D]], [0x256c, [D, D, D, D]],
  [0x256d, [0, L, 0, L]], [0x256e, [0, L, L, 0]],
  [0x256f, [L, 0, L, 0]], [0x2570, [L, 0, 0, L]],
])

function spec(up = 0, down = 0, left = 0, right = 0,
              shade = 0, fillEighths = 0, fillEdge = 0): BoxSpec {
  return Object.freeze({ up, down, left, right, shade, fillEighths, fillEdge })
}

// At most 65 native-supported codepoints enter this cache. Returning the same
// frozen value avoids allocating a BoxSpec for every procedural cell.
const SPEC_CACHE = new Map<number, BoxSpec>()

export function boxSpec(codepoint: number): BoxSpec | null {
  const cached = SPEC_CACHE.get(codepoint)
  if (cached !== undefined) return cached
  const line = LINE_SPECS.get(codepoint)
  let result: BoxSpec | null = null
  if (line !== undefined) result = spec(line[0], line[1], line[2], line[3])
  else if (codepoint === 0x2588) result = spec(0, 0, 0, 0, 0, 8, 1)
  else if (codepoint === 0x2580) result = spec(0, 0, 0, 0, 0, 4, 1)
  else if (codepoint >= 0x2581 && codepoint <= 0x2587) result = spec(0, 0, 0, 0, 0, codepoint - 0x2580, 2)
  else if (codepoint >= 0x2589 && codepoint <= 0x258f) result = spec(0, 0, 0, 0, 0, 8 - (codepoint - 0x2588), 3)
  else if (codepoint === 0x2590) result = spec(0, 0, 0, 0, 0, 4, 4)
  else if (codepoint === 0x2591) result = spec(0, 0, 0, 0, 64)
  else if (codepoint === 0x2592) result = spec(0, 0, 0, 0, 128)
  else if (codepoint === 0x2593) result = spec(0, 0, 0, 0, 192)
  if (result !== null) SPEC_CACHE.set(codepoint, result)
  return result
}

function lineThickness(cellHeight: number, weight: number): number {
  const thickness = Math.max(1, Math.trunc(cellHeight / 12))
  return weight === H ? thickness * 2 : thickness
}

export interface BoxRectSource {
  readonly spec: BoxSpec
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export type BoxRectVisitor<Source extends BoxRectSource, Context> = (
  context: Context,
  source: Source,
  x: number,
  y: number,
  width: number,
  height: number,
  alpha: number,
) => void

function emitBoxRect<Source extends BoxRectSource, Context>(
  source: Source,
  context: Context,
  visit: BoxRectVisitor<Source, Context>,
  x: number,
  y: number,
  width: number,
  height: number,
  alpha = 255,
): void {
  if (width <= 0 || height <= 0) return
  visit(context, source, x, y, width, height, alpha)
}

function visitLineArm<Source extends BoxRectSource, Context>(
  source: Source,
  context: Context,
  visit: BoxRectVisitor<Source, Context>,
  direction: 'left' | 'right' | 'up' | 'down',
  weight: number,
): void {
  if (weight === 0) return
  const { x, y, width, height } = source
  const thickness = lineThickness(height, weight)
  const middleX = x + Math.trunc((width - thickness) / 2)
  const middleY = y + Math.trunc((height - thickness) / 2)
  const horizontal = direction === 'left' || direction === 'right'
  const start = direction === 'left' || direction === 'up'
  const length = horizontal
    ? (start ? middleX - x + thickness : x + width - middleX)
    : (start ? middleY - y + thickness : y + height - middleY)
  if (weight === D) {
    const gap = thickness + 1
    if (horizontal) {
      emitBoxRect(source, context, visit, start ? x : middleX, middleY - gap,
        length, thickness)
      emitBoxRect(source, context, visit, start ? x : middleX, middleY + gap,
        length, thickness)
    } else {
      emitBoxRect(source, context, visit, middleX - gap, start ? y : middleY,
        thickness, length)
      emitBoxRect(source, context, visit, middleX + gap, start ? y : middleY,
        thickness, length)
    }
  } else if (horizontal) {
    emitBoxRect(source, context, visit, start ? x : middleX, middleY, length, thickness)
  } else {
    emitBoxRect(source, context, visit, middleX, start ? y : middleY, thickness, length)
  }
}

// Allocation-free procedural expansion for renderer consumption. JavaScript's
// Math.trunc matches C integer division for negative intermediate values.
export function visitBoxRects<Source extends BoxRectSource, Context>(
  source: Source,
  context: Context,
  visit: BoxRectVisitor<Source, Context>,
): void {
  const value = source.spec
  const { x, y, width, height } = source
  if (value.shade !== 0) {
    emitBoxRect(source, context, visit, x, y, width, height, value.shade)
    return
  }
  if (value.fillEighths !== 0) {
    let rectX = x
    let rectY = y
    let rectWidth = width
    let rectHeight = height
    if (value.fillEdge === 1) rectHeight = Math.trunc(height * value.fillEighths / 8)
    if (value.fillEdge === 2) {
      rectHeight = Math.trunc(height * value.fillEighths / 8)
      rectY = y + height - rectHeight
    }
    if (value.fillEdge === 3) rectWidth = Math.trunc(width * value.fillEighths / 8)
    if (value.fillEdge === 4) {
      rectWidth = Math.trunc(width * value.fillEighths / 8)
      rectX = x + width - rectWidth
    }
    emitBoxRect(source, context, visit, rectX, rectY, rectWidth, rectHeight)
    return
  }
  visitLineArm(source, context, visit, 'left', value.left)
  visitLineArm(source, context, visit, 'right', value.right)
  visitLineArm(source, context, visit, 'up', value.up)
  visitLineArm(source, context, visit, 'down', value.down)
}

interface BoxRectCollection {
  readonly result: BoxRect[]
  readonly color: GridRgb
}

function collectBoxRect(context: BoxRectCollection, _source: BoxRectSource,
                        x: number, y: number, width: number, height: number,
                        alpha: number): void {
  context.result.push({ kind: 'rect', layer: 'content', primitive: 'box',
    x, y, width, height, color: context.color, alpha })
}

export function boxRects(value: BoxSpec, x: number, y: number, width: number,
                         height: number, color: GridRgb): BoxRect[] {
  const result: BoxRect[] = []
  visitBoxRects({ spec: value, x, y, width, height }, { result, color }, collectBoxRect)
  return result
}
